import React, { ReactNode } from "react";
import {
  Autocomplete,
  Chip,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import theme from "../../theme";

interface Props {
  control: Control<any, any>;
  fieldName: string;
  options: any[];
  required?: boolean;
  label: string;
  disabled?: boolean;
  noOptionsText?: string;
  multiple?: boolean;
  renderOption?: (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => ReactNode;
  onChange?: (value: any) => void;
  defaultValue?: any[] | any;
  staticFilter?: boolean;
  freeSolo?: boolean;
}

const FormAutocomplete: React.FC<Props> = (props) => {
  const {
    control,
    fieldName,
    options,
    required,
    label,
    disabled,
    noOptionsText,
    multiple,
    renderOption,
    onChange,
    defaultValue,
    staticFilter,
    freeSolo,
  } = props;

  const handleOnChange = (value: any) => {
    if (onChange) {
      onChange(value);
    }
  };

  const defaultRenderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => {
    return (
      <li
        {...props}
        style={{ backgroundColor: `${theme.palette.background.paper}` }}
      >
        {option?.value || option?.name || option?.type || option || ""}
      </li>
    );
  };

  const customPopper = function (props: any) {
    return <Popper {...props} placement="bottom" />;
  };

  const keyOf = (x: any) => {
    const label =
      typeof x === "string"
        ? x
        : x?.name ||
          x?.value ||
          x?.type ||
          [x?.firstName, x?.lastName].filter(Boolean).join(" ") ||
          x?.id?.toString?.() ||
          "";

    const m = String(label).match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i);
    if (m) return m[0].toLowerCase();

    if (typeof x === "string") return x.trim().toLowerCase();
    if (x?.email) return String(x.email).trim().toLowerCase();
    if (x?.stringId) return String(x.stringId).trim().toLowerCase();
    if (x?.value) return String(x.value).trim().toLowerCase();
    if (x?.id != null) return String(x.id).trim().toLowerCase();
    return String(label).trim().toLowerCase();
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required: {
          value: required!,
          message: "Required",
        },
      }}
      render={({ field, fieldState }) => {
        return (
          <Autocomplete
            {...field}
            key={JSON.stringify(options)}
            multiple={!!multiple}
            disableCloseOnSelect={!!multiple && options?.length! > 1}
            freeSolo={freeSolo}
            options={options}
            filterOptions={!!staticFilter ? undefined : (x) => x}
            filterSelectedOptions
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option?.name ||
                  option?.value ||
                  option?.type ||
                  `${option?.firstName} ${option.lastName}` ||
                  option?.id?.toString() ||
                  ""
            }
            noOptionsText={
              <Typography color="primary">
                {noOptionsText ? noOptionsText : "No Results"}
              </Typography>
            }
            isOptionEqualToValue={(option, value) =>
              keyOf(option) === keyOf(value)
            }
            PopperComponent={customPopper}
            readOnly={disabled}
            disabled={disabled}
            renderOption={renderOption || defaultRenderOption}
            fullWidth
            renderTags={
              multiple
                ? (value: any[], getItemProps) =>
                    value.map((option, index: number) => {
                      const { key: itemKey, ...itemProps } = getItemProps({
                        index,
                      });
                      const label =
                        typeof option === "string"
                          ? option
                          : option?.name ||
                            option?.value ||
                            option?.type ||
                            `${option?.firstName} ${option?.lastName}` ||
                            option?.id?.toString() ||
                            "";
                      return (
                        <Tooltip key={itemKey} title={label}>
                          <Chip
                            variant="outlined"
                            label={label}
                            {...itemProps}
                          />
                        </Tooltip>
                      );
                    })
                : undefined
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={label}
                helperText={fieldState.error ? fieldState.error.message : null}
                error={!!fieldState.error}
                variant="outlined"
                type={"text"}
                onKeyDown={(e) => {
                  const k = e.key;
                  const isDelete = k === "Backspace" || k === "Delete";
                  const target = e.target as HTMLInputElement;
                  const isEmpty = (target.value ?? "") === "";
                  if (isDelete && multiple && isEmpty) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                }}
              />
            )}
            value={
              freeSolo
                ? (field.value ?? (multiple ? [] : null))
                : (defaultValue ??
                  (!!multiple && !!field.value && options
                    ? options?.filter((x) =>
                        field?.value?.some((v: any) => v.id === x.id),
                      )
                    : !multiple && !!field.value
                      ? options && options.find((x) => x.id === field.value.id)
                      : !!multiple && !field?.value
                        ? []
                        : null))
            }
            onChange={(_, value) => {
              let val = value;
              if (multiple && value?.some((x: any) => x.id === -1)) {
                val = options.filter((x) => x.id !== -1);
              }
              field.onChange(val);
              handleOnChange(val);
            }}
          />
        );
      }}
    />
  );
};

export default FormAutocomplete;
